/**
 * #3368 arkin TOUCH 중국 발주 수량 계산.
 *
 * 공식: Suggested = max(0, Safety − (Available + In Transit + Pending PO)). Safety 가 비어 있으면 0.
 * - Available·In Transit·Pending PO·Safety(reorder_level)·FOB 는 GET touchOrderCalc 가 품목별로 준다.
 * - 셀 배정은 TOUCH Stock Sheet(#3293)와 같은 class code 접두 + 27색이다.
 * - Safety 입력·저장과 Final 수정은 품목이 정확히 하나인 칸에서만 한다. 저장 대상은 셀 합계가 아니라 그 칸의 item_id 다.
 * - Final 은 사용자가 고친 칸(final_overrides, item_id 키)만 고정되고, 나머지 칸은 Suggested 를 따른다.
 * - 주문서는 TOUCH Stock Sheet 와 같은 양식(buildTouchStockSheetCells)에 Final 수량과 FOB 단가를 싣는다.
 *
 * 이 파일은 프로젝트 모듈에 의존하지 않는 순수 로직만 담는다(단위 테스트 대상).
 */

import { TOUCH_BLOCKS, TOUCH_COLORS } from '@/utils/touchStockSheet';

export const TOUCH_ORDER_SHEET_NAME = 'ORDER SHEET';
export const TOUCH_ORDER_DETAIL_SHEET_NAME = 'CALC DETAIL';
export const TOUCH_ORDER_SHEET_TITLE = 'AH BUNDLE & CLOSURE ORDER SHEET';
/** reorder_level(int) 입력 상한. 서버(ItemMasterService.TOUCH_SAFETY_STOCK_MAX_VALUE)와 같다. */
export const TOUCH_SAFETY_STOCK_MAX = 1000000;
export const TOUCH_FINAL_ORDER_MAX = 1000000;

export const TOUCH_ORDER_VIEWS = [
	{ key: 'final', label: 'Final Order' },
	{ key: 'suggested', label: 'Suggested' },
	{ key: 'safety', label: 'Safety Stock' },
	{ key: 'available', label: 'Available' },
	{ key: 'in_transit', label: 'In Transit' },
	{ key: 'pending', label: 'Pending PO' },
];

const UNMAPPED_SAMPLE_LIMIT = 5;

function isNumeric(value) {
	return value !== null && value !== undefined && value !== '' && !isNaN(Number(value));
}

function toNumber(value) {
	return isNumeric(value) ? Number(value) : 0;
}

/** reorder_level 은 null(미설정) 과 0 을 구분한다. */
function toSafety(value) {
	return isNumeric(value) ? Number(value) : null;
}

function round2(value) {
	return Math.round((value + Number.EPSILON) * 100) / 100;
}

function hasOwn(object, key) {
	return Object.prototype.hasOwnProperty.call(object || {}, key);
}

/** 화면 입력을 정수로 읽는다. 앞뒤 공백·천 단위 쉼표는 허용하고, 소수·음수·문자는 거부한다. */
function parseWholeNumber(text, max) {
	const trimmed = String(text === null || text === undefined ? '' : text)
		.trim()
		.replace(/,/g, '');
	if (!/^\d+$/.test(trimmed)) return { ok: false };
	const value = Number(trimmed);
	if (!Number.isSafeInteger(value) || value > max) return { ok: false };
	return { ok: true, value };
}

/** Safety 입력: 빈칸은 지우기(null), 0 이상 정수만 허용. */
export function parseSafetyInput(text) {
	if (String(text === null || text === undefined ? '' : text).trim() === '') return { ok: true, value: null };
	return parseWholeNumber(text, TOUCH_SAFETY_STOCK_MAX);
}

/** Final 입력: 빈칸은 0, 0 이상 정수만 허용. */
export function parseFinalInput(text) {
	if (String(text === null || text === undefined ? '' : text).trim() === '') return { ok: true, value: 0 };
	return parseWholeNumber(text, TOUCH_FINAL_ORDER_MAX);
}

export function suggestedQuantity(safety, available, in_transit, pending) {
	if (safety === null || safety === undefined) return 0;
	return Math.max(0, safety - (available + in_transit + pending));
}

function emptyCell(key) {
	return { key, items: [], item_id: null, item_code: '', status: 'empty', available: 0, in_transit: 0, pending: 0, safety: null, fob: 0 };
}

/**
 * 품목 행을 블록 × 행 × 27색 칸에 정확히 한 번씩 배정한다.
 * 칸 상태: empty(품목 없음) / single(품목 1개, 입력 가능) / multiple(2개 이상, 합계만 표시·입력 불가).
 * 배정되지 않은 품목은 Available·Safety·In Transit·Pending 중 하나라도 0 이 아니면 경고 대상이다.
 */
export function buildTouchOrderMatrix(rows, options) {
	const include_value = !!(options && options.include_value);

	const color_index = new Map();
	TOUCH_COLORS.forEach((color, index) => color_index.set(color.code, index));

	const class_index = new Map();
	TOUCH_BLOCKS.forEach((block, block_index) => block.groups.forEach((group, group_index) => group.lengths.forEach((length, row_index) => class_index.set(group.class_prefix + length, { block_index, group_index, row_index }))));

	const blocks = TOUCH_BLOCKS.map(block => ({
		key: block.key,
		groups: block.groups.map(group => ({
			key: group.key,
			rows: group.lengths.map(length => ({
				key: `${block.key}|${group.key}|${length}`,
				length,
				label: group.label_prefix + length,
				cells: TOUCH_COLORS.map(color => emptyCell(`${block.key}|${group.key}|${length}|${color.code}`)),
			})),
		})),
	}));

	const unmapped = { row_count: 0, samples: [] };

	(rows || []).forEach(row => {
		const item = {
			item_id: row.item_id === null || row.item_id === undefined ? '' : String(row.item_id),
			item_code: row.item_code === null || row.item_code === undefined ? '' : String(row.item_code),
			available: toNumber(row.available_quantity),
			in_transit: toNumber(row.in_transit_quantity),
			pending: toNumber(row.pending_po_quantity),
			safety: toSafety(row.reorder_level),
			fob: include_value ? toNumber(row.fob) : 0,
		};

		const class_code = String(row.class_code === null || row.class_code === undefined ? '' : row.class_code).trim();
		const color_code = String(row.color_code === null || row.color_code === undefined ? '' : row.color_code).trim();
		const position = class_index.get(class_code);
		const column = color_index.get(color_code);

		if (!position || column === undefined) {
			if (item.available !== 0 || item.in_transit !== 0 || item.pending !== 0 || (item.safety !== null && item.safety !== 0)) {
				unmapped.row_count++;
				if (unmapped.samples.length < UNMAPPED_SAMPLE_LIMIT) unmapped.samples.push({ class_code, color_code, available: item.available, safety: item.safety, in_transit: item.in_transit, pending: item.pending });
			}
			return;
		}

		const cell = blocks[position.block_index].groups[position.group_index].rows[position.row_index].cells[column];
		cell.items.push(item);
		cell.available += item.available;
		cell.in_transit += item.in_transit;
		cell.pending += item.pending;
		if (item.safety !== null) cell.safety = (cell.safety === null ? 0 : cell.safety) + item.safety;
		if (cell.items.length === 1) {
			cell.status = 'single';
			cell.item_id = item.item_id;
			cell.item_code = item.item_code;
			cell.fob = item.fob;
		} else {
			// 품목을 하나로 정할 수 없으면 저장·수정 대상에서 뺀다. FOB 금액은 품목별 Suggested 기준으로 따로 계산할 수 없어 0 으로 둔다.
			cell.status = 'multiple';
			cell.item_id = null;
			cell.item_code = cell.items.map(entry => entry.item_code).join(', ');
			cell.fob = 0;
		}
	});

	return { blocks, colors: TOUCH_COLORS, include_value, unmapped };
}

/** 편집 가능한 칸: 품목이 정확히 하나. */
export function isEditableCell(cell) {
	return cell.status === 'single' && !!cell.item_id;
}

export function cellSafety(cell, safety_edits) {
	if (isEditableCell(cell) && hasOwn(safety_edits, cell.item_id)) return safety_edits[cell.item_id];
	return cell.safety;
}

export function cellSuggested(cell, safety_edits) {
	return suggestedQuantity(cellSafety(cell, safety_edits), cell.available, cell.in_transit, cell.pending);
}

export function isFinalOverridden(cell, final_overrides) {
	return isEditableCell(cell) && hasOwn(final_overrides, cell.item_id);
}

export function cellFinal(cell, safety_edits, final_overrides) {
	if (isFinalOverridden(cell, final_overrides)) return final_overrides[cell.item_id];
	return cellSuggested(cell, safety_edits);
}

/** 보기별 칸 값. Safety 는 미설정이면 null(빈칸)이다. */
export function cellViewValue(cell, view, safety_edits, final_overrides) {
	switch (view) {
		case 'final':
			return cellFinal(cell, safety_edits, final_overrides);
		case 'suggested':
			return cellSuggested(cell, safety_edits);
		case 'safety':
			return cellSafety(cell, safety_edits);
		case 'available':
			return cell.available;
		case 'in_transit':
			return cell.in_transit;
		case 'pending':
			return cell.pending;
		default:
			return 0;
	}
}

/**
 * 한 칸의 Safety 편집값을 반영한 새 편집 객체를 돌려준다. 저장된 값과 같아지면 편집에서 뺀다.
 * 편집 불가 칸은 그대로 둔다.
 */
export function setSafetyEdit(safety_edits, cell, value) {
	if (!isEditableCell(cell)) return safety_edits;
	const next = Object.assign({}, safety_edits);
	if (value === cell.safety) delete next[cell.item_id];
	else next[cell.item_id] = value;
	return next;
}

/** 행 일괄 입력: 그 행에서 품목이 하나인 칸에만 같은 값을 넣는다. */
export function applyRowSafety(safety_edits, row, value) {
	return row.cells.reduce((edits, cell) => setSafetyEdit(edits, cell, value), safety_edits);
}

export function setFinalOverride(final_overrides, cell, value) {
	if (!isEditableCell(cell)) return final_overrides;
	return Object.assign({}, final_overrides, { [cell.item_id]: value });
}

/** 저장 요청 본문. 편집된 품목만 보낸다. */
export function safetyEditPayload(safety_edits) {
	return Object.keys(safety_edits || {}).map(item_id => ({ item_id, reorder_level: safety_edits[item_id] }));
}

export function safetyEditCount(safety_edits) {
	return Object.keys(safety_edits || {}).length;
}

/** 재조회 뒤 사라진 품목의 Final 고정값은 버린다. 남은 품목의 값은 그대로 유지한다. */
export function pruneFinalOverrides(matrix, final_overrides) {
	const editable = new Set();
	matrix.blocks.forEach(block => block.groups.forEach(group => group.rows.forEach(row => row.cells.forEach(cell => isEditableCell(cell) && editable.add(cell.item_id)))));
	const next = {};
	Object.keys(final_overrides || {}).forEach(item_id => {
		if (editable.has(item_id)) next[item_id] = final_overrides[item_id];
	});
	return next;
}

/**
 * TOUCH Stock Sheet 양식(buildTouchStockSheetCells)이 그대로 받는 모양의 주문 매트릭스.
 * 셀 수량 = Final, 금액 = Final × FOB(금액 권한일 때만).
 * Final 이 있는데 FOB 가 0 인 칸은 TTL PRICE 가 그만큼 작아지므로 zero_fob 로 센다.
 */
export function buildOrderSheetMatrix(matrix, safety_edits, final_overrides) {
	const include_value = matrix.include_value;
	const zero_fob = { cells: 0, quantity: 0 };
	const blocks = matrix.blocks.map(block => {
		const color_quantities = TOUCH_COLORS.map(() => 0);
		const color_values = TOUCH_COLORS.map(() => 0);
		let quantity = 0;
		let value = 0;
		const groups = block.groups.map(group => ({
			key: group.key,
			rows: group.rows.map(row => ({
				length: row.length,
				label: row.label,
				cells: row.cells.map((cell, color_index) => {
					const final_quantity = cellFinal(cell, safety_edits, final_overrides);
					const final_value = include_value ? final_quantity * cell.fob : 0;
					if (include_value && final_quantity !== 0 && final_value === 0) {
						zero_fob.cells++;
						zero_fob.quantity += final_quantity;
					}
					color_quantities[color_index] += final_quantity;
					color_values[color_index] += final_value;
					quantity += final_quantity;
					value += final_value;
					return { quantity: final_quantity, value: final_value };
				}),
			})),
		}));
		return { key: block.key, groups, color_quantities, color_values, quantity, value };
	});

	const total_quantity = blocks.reduce((sum, block) => sum + block.quantity, 0);
	const total_value = blocks.reduce((sum, block) => sum + block.value, 0);
	return { blocks, colors: TOUCH_COLORS, include_value, total_quantity, total_value: round2(total_value), zero_fob };
}

/** 예산 차액. 예산이 비었거나 잘못되면 null. 양수 = 남음, 음수 = 초과. */
export function budgetDifference(budget_text, total_value) {
	const trimmed = String(budget_text === null || budget_text === undefined ? '' : budget_text)
		.trim()
		.replace(/[$,]/g, '');
	if (trimmed === '' || !/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
	return round2(Number(trimmed) - total_value);
}

export function touchOrderUnmappedMessage(matrix) {
	const unmapped = matrix.unmapped;
	if (!unmapped || unmapped.row_count === 0) return '';
	const samples = unmapped.samples.map(sample => `${sample.class_code}/${sample.color_code}(A ${sample.available}, S ${sample.safety === null ? '-' : sample.safety}, T ${sample.in_transit}, P ${sample.pending})`).join(', ');
	const more = unmapped.row_count > unmapped.samples.length ? ' ...' : '';
	return `NOT ON THIS SHEET: ${unmapped.row_count} item(s) - ${samples}${more}`;
}

export function touchOrderZeroFobMessage(order_matrix) {
	if (!order_matrix.include_value || !order_matrix.zero_fob || order_matrix.zero_fob.cells === 0) return '';
	return `NO FOB: ${order_matrix.zero_fob.cells} item(s), qty ${order_matrix.zero_fob.quantity} counted at $0 - TTL PRICE is lower by that much.`;
}

export function touchOrderNoteText(matrix, order_matrix) {
	return [touchOrderUnmappedMessage(matrix), touchOrderZeroFobMessage(order_matrix)].filter(Boolean).join('  |  ');
}

/** 계산 상세 시트(2시트). 품목이 있는 칸마다 한 행. FOB 열은 금액 권한일 때만. */
export function buildTouchOrderDetailRows(matrix, safety_edits, final_overrides) {
	const include_value = matrix.include_value;
	const header = ['Block', 'Line', 'Length', 'Color', 'Item Code', 'Available', 'In Transit', 'Pending PO', 'Safety Stock', 'Suggested', 'Final Order'];
	if (include_value) header.push('FOB', 'FOB Amount');
	const rows = [header];
	matrix.blocks.forEach(block =>
		block.groups.forEach(group =>
			group.rows.forEach(row =>
				row.cells.forEach((cell, color_index) => {
					if (cell.status === 'empty') return;
					const safety = cellSafety(cell, safety_edits);
					const final_quantity = cellFinal(cell, safety_edits, final_overrides);
					const detail = [block.key, group.key, row.length, TOUCH_COLORS[color_index].label, cell.item_code, cell.available, cell.in_transit, cell.pending, safety === null ? '' : safety, cellSuggested(cell, safety_edits), final_quantity];
					if (include_value) detail.push(cell.status === 'single' ? cell.fob : '', round2(final_quantity * cell.fob));
					rows.push(detail);
				}),
			),
		),
	);
	return rows;
}

/** touchOrderSheet_YYYYMMDD.xlsx */
export function touchOrderFileName(report_date) {
	const date = report_date || new Date();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `touchOrderSheet_${date.getFullYear()}${month}${day}.xlsx`;
}
